import * as React from 'react';
import { connect } from 'react-redux';
import * as _ from 'underscore';

import { BrowserTitleService } from '~commonInfra/services/BrowserTitleService';
import { SessionServerSelectors, SessionUserSelectors } from '~selectors/SessionInfoSelectors';
import {
  IntegrationsSiteSettingsSubspaceComponent,
  IntegrationsSubspaceName
} from '~src/app/spaces/IntegrationsSettings/IntegrationsSiteSettingsSubspace';
import { Messages } from '~src/codegen/Localize';
import { HomeUserUtils } from '~src/home/utils/HomeUserUtils';
import { SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { ServerSettingsInstance } from '~src/settings/services/ServerSettings';
import { ServerSettingsUnauthenticated } from '~src/settings/services/ServerSettingsUnauthenticated';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { SiteRoles } from '~src/ts/ServerApi/types/ISiteRole';
import { Sites } from '~src/ts/siteManagement/services/resources/Sites';

import { AppState } from '../../state/AppState';
import { SiteSettingsSubspaceName } from '../../state/SpaceInfo';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { Subspace, TabbedSpace } from '../TabbedSpace';
import { AuthenticationSiteSettingsSubspace } from './AuthenticationSettingsSubspace';
import { BridgeSiteSettingsSubspace } from './BridgeSiteSettingsSubspace';
import { ExtensionsSiteSettingsSubspaceComponent } from './ExtensionsSiteSettingsSubspace';
import { GeneralSiteSettingsSubspace } from './GeneralSiteSettingsSubspace';

export interface SiteSettingsSpaceOwnProps {
  readonly urls: SpacesSiteUrls;
  readonly siteName: string;
  readonly subspaceName: SiteSettingsSubspaceName;
}

export interface SiteSettingsServicesProps {
  readonly server: SessionManagementService;
}

export interface SiteSettingsStateProps {
  readonly isSessionMultiSite: boolean;
  readonly isSessionServerAdmin: boolean;
  readonly userRole: string;
  readonly isOnlineEnvironment: boolean;
}

export type SiteSettingsNonServicesProps = SiteSettingsSpaceOwnProps & SiteSettingsStateProps;
export type SiteSettingsSpaceProps = SiteSettingsNonServicesProps & SiteSettingsServicesProps;

export class SiteSettingsSpaceComponent extends React.PureComponent<SiteSettingsSpaceProps> {

  public componentDidMount() {
    BrowserTitleService.setTitleWithTranslatedSpace({
      translatedSpaceName: this.props.isSessionMultiSite ? Messages.siteSettings() : Messages.settings()
    });
  }

  public render() {
    const subspaces = getSettingsSubspaces(
      this.props.urls,
      this.props.siteName,
      this.props.server,
      this.props.isSessionMultiSite,
      this.props.isSessionServerAdmin,
      this.props.userRole,
      this.props.isOnlineEnvironment
    );

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.subspaceName}
        urlForSubspace={this.getSettingsUrl}
        mastheadElement={null}
        testIdPrefix={'site-settings'}
        spaceItemName={''}
      />
    );
  }

  private static settingsUrlBySubspace: Record<SiteSettingsSubspaceName, (urls: SpacesSiteUrls) => string> = {
    settings: (urls) => urls.settingsUrl(),
    extensions: (urls) =>  urls.settingsExtensionsUrl(),
    bridge: (urls) =>  urls.settingsBridgeUrl(),
    authentication: (urls) =>  urls.settingsAuthenticationUrl(),
    integrations: (urls) => urls.settingsIntegrationsUrl(),
  };

  private getSettingsUrl = (subspaceName?: SiteSettingsSubspaceName) => {
    if (subspaceName) {
      return SiteSettingsSpaceComponent.settingsUrlBySubspace[subspaceName](this.props.urls);
    }
    return '';
  }
}

export function getSettingsSubspaces(
  urls: SpacesSiteUrls,
  siteName: string,
  server: SessionManagementService,
  isSessionMultiSite: boolean,
  isSessionServerAdmin: boolean,
  userRole: string,
  isOnlineEnvironment: boolean
): ReadonlyArray<Subspace> {
  return _.compact([
    {
      name: 'settings',
      tabLabel: Messages.general(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <GeneralSiteSettingsSubspace
          siteName={siteName}
          urls={urls}
          server={server}
          isSessionMultiSite={isSessionMultiSite}
          isSessionServerAdmin={isSessionServerAdmin}
          serverSettingsService={ServerSettingsInstance}
        />
      )
    },
    ServerSettingsUnauthenticated.isSiteSamlEnabled() && {
      name: 'authentication',
      tabLabel: Messages.authentication(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <AuthenticationSiteSettingsSubspace
          siteName={siteName}
          urls={urls}
          server={server}
          isSessionMultiSite={isSessionMultiSite}
          isSessionServerAdmin={isSessionServerAdmin}
          serverSettingsService={ServerSettingsInstance}
          sites={Sites.instance}
        />
      )
    },
    ServerSettingsUnauthenticated.isBridgeEnabled() && {
      name: 'bridge',
      tabLabel: Messages.bridge(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <BridgeSiteSettingsSubspace
          siteName={siteName}
          urls={urls}
          server={server}
          isSessionMultiSite={isSessionMultiSite}
          isSessionServerAdmin={isSessionServerAdmin}
        />
      )
    },
    FeatureFlags.extensionsSettingsTabEnabled && {
      name: 'extensions',
      tabLabel: Messages.extensionsSettings_title(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <ExtensionsSiteSettingsSubspaceComponent
          siteName={siteName}
          urls={urls}
          server={server}
          isSessionMultiSite={isSessionMultiSite}
          isSessionServerAdmin={isSessionServerAdmin}
        />
      )
    },
    isIntegrationsTabEnabledForSiteSettings() && isUserAuthorizedForIntegrationsTab(userRole) && {
      name: IntegrationsSubspaceName,
      tabLabel: Messages.integrations_settingsTabTitle(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <IntegrationsSiteSettingsSubspaceComponent
          siteName={siteName}
          urls={urls}
          server={server}
          isSessionMultiSite={isSessionMultiSite}
          isSessionServerAdmin={isSessionServerAdmin}
          isOnlineEnvironment={isOnlineEnvironment}
          userRole={userRole}
        />
      )
    },
  ]);
}

export function isIntegrationsTabEnabledForSiteSettings() {
  const isSalesforceIntegrationEnabled = FeatureFlags.publishingSalesforceBundlesEnabled;
  const isSlackIntegrationEnabled = FeatureFlags.userNotificationSlackChannel;
  return isSalesforceIntegrationEnabled || isSlackIntegrationEnabled;
}

export function isUserAuthorizedForIntegrationsTab(userRole: String) {
  const isUserServerAdmin = (userRole === SiteRoles.ServerAdministrator);
  const isUserSiteAdminCreator = (userRole === SiteRoles.SiteAdministratorCreator);
  const isUserSiteAdminExplorer = (userRole === SiteRoles.SiteAdministratorExplorer);

  return (isUserServerAdmin || isUserSiteAdminCreator || (isUserSiteAdminExplorer && FeatureFlags.userNotificationSlackChannel));
}

export const ConnectedSiteSettingsSpace: React.ComponentClass<SiteSettingsSpaceOwnProps & SiteSettingsServicesProps>
  = connect((state: AppState) => ({
    isSessionMultiSite: SessionUserSelectors.isSessionMultiSite(state),
    isSessionServerAdmin: SessionUserSelectors.isSessionServerAdmin(state),
    userRole: HomeUserUtils.getRole(state),
    isOnlineEnvironment: SessionServerSelectors.isOnlineEnvironment(state),
  }))(SiteSettingsSpaceComponent);